import { useState, useEffect } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

export default function SettingsEmailPage() {
    const { data, mutate } = useSWR<any>('/api/email-config', fetcher)
    const [tab, setTab] = useState<'smtp' | 'mailgun'>('smtp')
    const [msg, setMsg] = useState<string | null>(null)

    useEffect(() => {
        if (data?.providerType === 'Mailgun') setTab('mailgun')
    }, [data])

    return (
        <div>
            <h1>Email Configuration</h1>
            <p className="muted">Configure how this user sends mail. Either SMTP or Mailgun. Credentials are encrypted at rest.</p>

            <div className="row" style={{ marginBottom: 12 }}>
                <button className={tab === 'smtp' ? '' : 'secondary'} onClick={() => setTab('smtp')}>SMTP</button>
                <button className={tab === 'mailgun' ? '' : 'secondary'} onClick={() => setTab('mailgun')}>Mailgun</button>
                {data?.isConfigured && <span className="success">Configured ✓</span>}
            </div>

            {msg && <div className={msg.startsWith('Error') ? 'error' : 'success'}>{msg}</div>}

            {tab === 'smtp' ? <SmtpForm existing={data} onSaved={(m) => { setMsg(m); mutate() }} /> :
                <MailgunForm existing={data} onSaved={(m) => { setMsg(m); mutate() }} />}

            {data?.isConfigured && <TestSection onMsg={setMsg} />}
        </div>
    )
}

function SmtpForm({ existing, onSaved }: { existing: any, onSaved: (m: string) => void }) {
    const [form, setForm] = useState({
        host: '', port: 587, username: '', password: '',
        useTls: true, fromEmail: '', fromName: '', replyToEmail: '', signature: ''
    })
    useEffect(() => {
        if (existing?.providerType === 'Smtp') {
            setForm(f => ({
                ...f,
                host: existing.smtpHost ?? '',
                port: existing.smtpPort ?? 587,
                username: existing.smtpUsername ?? '',
                useTls: existing.smtpUseTls ?? true,
                fromEmail: existing.fromEmail ?? '',
                fromName: existing.fromName ?? '',
                replyToEmail: existing.replyToEmail ?? '',
                signature: existing.signature ?? ''
            }))
        }
    }, [existing])

    async function save(e: React.FormEvent) {
        e.preventDefault()
        try {
            await api('/api/email-config/smtp', { method: 'POST', body: JSON.stringify(form) })
            onSaved('SMTP config saved.')
        } catch (e: any) { onSaved(`Error: ${e.message}`) }
    }

    return (
        <form onSubmit={save} className="card">
            <div className="split">
                <div className="form-row"><label>Host</label><input required value={form.host} onChange={e => setForm({ ...form, host: e.target.value })} placeholder="smtp.example.com" /></div>
                <div className="form-row"><label>Port</label><input type="number" required value={form.port} onChange={e => setForm({ ...form, port: parseInt(e.target.value) || 587 })} /></div>
                <div className="form-row"><label>Username</label><input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} /></div>
                <div className="form-row"><label>Password {existing?.providerType === 'Smtp' && '(leave blank to keep existing)'}</label><input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} /></div>
                <div className="form-row"><label>From Email</label><input type="email" required value={form.fromEmail} onChange={e => setForm({ ...form, fromEmail: e.target.value })} /></div>
                <div className="form-row"><label>From Name</label><input value={form.fromName} onChange={e => setForm({ ...form, fromName: e.target.value })} /></div>
                <div className="form-row"><label>Reply-To (optional)</label><input value={form.replyToEmail} onChange={e => setForm({ ...form, replyToEmail: e.target.value })} /></div>
                <div className="form-row"><label><input type="checkbox" checked={form.useTls} onChange={e => setForm({ ...form, useTls: e.target.checked })} /> Use STARTTLS</label></div>
            </div>
            <div className="form-row"><label>Signature (HTML)</label><textarea value={form.signature} onChange={e => setForm({ ...form, signature: e.target.value })} /></div>
            <button type="submit" data-testid="save-smtp">Save SMTP Config</button>
        </form>
    )
}

function MailgunForm({ existing, onSaved }: { existing: any, onSaved: (m: string) => void }) {
    const [form, setForm] = useState({
        apiKey: '', domain: '', fromEmail: '', fromName: '', replyToEmail: '', signature: ''
    })
    useEffect(() => {
        if (existing?.providerType === 'Mailgun') {
            setForm(f => ({
                ...f,
                domain: existing.mailgunDomain ?? '',
                fromEmail: existing.fromEmail ?? '',
                fromName: existing.fromName ?? '',
                replyToEmail: existing.replyToEmail ?? '',
                signature: existing.signature ?? ''
            }))
        }
    }, [existing])

    async function save(e: React.FormEvent) {
        e.preventDefault()
        try {
            await api('/api/email-config/mailgun', { method: 'POST', body: JSON.stringify(form) })
            onSaved('Mailgun config saved.')
        } catch (e: any) { onSaved(`Error: ${e.message}`) }
    }

    return (
        <form onSubmit={save} className="card">
            <div className="split">
                <div className="form-row"><label>API Key {existing?.providerType === 'Mailgun' && '(leave blank to keep existing)'}</label><input type="password" value={form.apiKey} onChange={e => setForm({ ...form, apiKey: e.target.value })} /></div>
                <div className="form-row"><label>Domain</label><input required value={form.domain} onChange={e => setForm({ ...form, domain: e.target.value })} placeholder="mg.example.com" /></div>
                <div className="form-row"><label>From Email</label><input type="email" required value={form.fromEmail} onChange={e => setForm({ ...form, fromEmail: e.target.value })} /></div>
                <div className="form-row"><label>From Name</label><input value={form.fromName} onChange={e => setForm({ ...form, fromName: e.target.value })} /></div>
                <div className="form-row"><label>Reply-To (optional)</label><input value={form.replyToEmail} onChange={e => setForm({ ...form, replyToEmail: e.target.value })} /></div>
            </div>
            <div className="form-row"><label>Signature (HTML)</label><textarea value={form.signature} onChange={e => setForm({ ...form, signature: e.target.value })} /></div>
            <button type="submit">Save Mailgun Config</button>
        </form>
    )
}

function TestSection({ onMsg }: { onMsg: (m: string) => void }) {
    const [recipient, setRecipient] = useState('')
    const [busy, setBusy] = useState(false)

    async function test() {
        setBusy(true)
        try {
            await api('/api/email-config/test', { method: 'POST', body: JSON.stringify({ recipient }) })
            onMsg('Test email sent — check your inbox.')
        } catch (e: any) { onMsg(`Error: ${e.message}`) }
        finally { setBusy(false) }
    }

    return (
        <div className="card">
            <h2>Send Test Email</h2>
            <div className="row">
                <input type="email" required value={recipient} onChange={e => setRecipient(e.target.value)} placeholder="recipient@example.com" className="grow" />
                <button onClick={test} disabled={busy || !recipient}>Send Test</button>
            </div>
        </div>
    )
}
